import { useCallback, useEffect, useState } from 'react';
import axiosInstance from '@libs/axios';

type ResolveResponse = { data: { accountCode: string } };

/**
 * Resolves the logged-in user's ppjk-credits account code — same link order
 * as Credit Topup: user_id → ceisa_user_id → ppjk_npwp.
 *
 * Returns `accountCode` only when a real credit account is linked. Does not
 * fall back to GATEWAY (callers that need a default should set it themselves).
 */
export default function useResolveCreditAccount() {
  const [accountCode, setAccountCode] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);

    try {
      const authData = localStorage.getItem('authData');
      if (authData) {
        const parsed = JSON.parse(authData);
        const userId = parsed.id;

        if (userId) {
          const response = await axiosInstance.get<ResolveResponse>(
            `/api/v1/ppjk-credits/resolve?linkType=user_id&linkValue=${userId}`,
            { signal }
          );
          if (response.data.data.accountCode) {
            setAccountCode(response.data.data.accountCode);
            return;
          }
        }

        const ceisaUserId = parsed.ceisaUserId;
        if (ceisaUserId) {
          const response = await axiosInstance.get<ResolveResponse>(
            `/api/v1/ppjk-credits/resolve?linkType=ceisa_user_id&linkValue=${ceisaUserId}`,
            { signal }
          );
          if (response.data.data.accountCode) {
            setAccountCode(response.data.data.accountCode);
            return;
          }
        }
      }

      const nomorPpjk = localStorage.getItem('nomor_ppjk');
      if (nomorPpjk) {
        const response = await axiosInstance.get<ResolveResponse>(
          `/api/v1/ppjk-credits/resolve?linkType=ppjk_npwp&linkValue=${nomorPpjk}`,
          { signal }
        );
        if (response.data.data.accountCode) {
          setAccountCode(response.data.data.accountCode);
          return;
        }
      }

      setAccountCode(undefined);
      setError('no credit account linked to this user');
    } catch (err: unknown) {
      const axiosErr = err as {
        name?: string;
        code?: string;
        message?: string;
        response?: { data?: { error?: string; message?: string } };
      };
      if (
        axiosErr.name === 'AbortError' ||
        axiosErr.name === 'CanceledError' ||
        axiosErr.code === 'ERR_CANCELED'
      ) {
        return;
      }
      setAccountCode(undefined);
      setError(
        axiosErr.response?.data?.error ||
          axiosErr.response?.data?.message ||
          axiosErr.message ||
          'resolve failed',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    void resolve(abortController.signal);
    return () => abortController.abort();
  }, [resolve]);

  return {
    accountCode,
    loading,
    error,
    /** True when a real (non-gateway) credit account is linked — payment flag active. */
    isPaymentActive: Boolean(accountCode),
    refetch: resolve,
  };
}
